import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Stack
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { enquiryService } from '../api/axios';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false); // State for maximize toggle
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatState, setChatState] = useState('initial');
  const [currentEnquiryId, setCurrentEnquiryId] = useState(null);
  const [enquiryItems, setEnquiryItems] = useState([]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen && messages.length === 0) {
      setMessages([{ sender: 'bot', text: 'Hello! I am Jarvis, your eYantra EMS assistant. How can I help you today?' }]);
    }
  };

  // Function to toggle the maximized state
  const handleToggleMaximize = () => {
    setIsMaximized(prev => !prev);
  };

  const addMessage = (sender, text) => {
    setMessages(prevMessages => [...prevMessages, { sender, text }]);
  };

  const handleUserInput = async (queryText) => {
    addMessage('user', queryText);
    setInput('');
    setIsLoading(true);

    let botResponseText = '';
    let nextChatState = chatState;

    try {
        if (chatState === 'initial') {
            if (queryText.toLowerCase() === 'certain enquiry') {
              botResponseText = "Great! Please provide the Enquiry ID (e.g., 'E-123') you need help with.";
              nextChatState = 'awaiting_enquiry_id';
            } else if (queryText.toLowerCase() === 'general recommendation') {
              botResponseText = "Okay, I can help with general product recommendations. Please type your query.";
              nextChatState = 'awaiting_general_query';
            } else {
              botResponseText = "I didn't understand. Please use the options provided.";
              nextChatState = 'initial';
            }
          } else if (chatState === 'awaiting_enquiry_id') {
            const uniqueEnquiryIdMatch = queryText.match(/[a-zA-Z0-9_-]+/);
            if (uniqueEnquiryIdMatch) {
              const uniqueEnquiryId = uniqueEnquiryIdMatch[0];
              const enquiryDetailsResponse = await enquiryService.get('/enquiry', {
                  params: { searchTerm: uniqueEnquiryId }
              });
              const fetchedEnquiry = enquiryDetailsResponse.data[0];

              if (fetchedEnquiry) {
                  setCurrentEnquiryId(fetchedEnquiry.id);
                  setEnquiryItems(fetchedEnquiry.items || []);
                  botResponseText = `Found enquiry ${fetchedEnquiry.uniqueId}. Do you want recommendations for the 'entire enquiry' or a 'specific item'?`;
                  nextChatState = 'awaiting_item_choice';
              } else {
                  botResponseText = `Sorry, I could not find an enquiry with ID: ${uniqueEnquiryId}. Please double-check the ID.`;
                  nextChatState = 'awaiting_enquiry_id';
              }
            } else {
              botResponseText = "I couldn't find a valid Enquiry ID in your message. Please provide a valid ID (e.g., E-123).";
              nextChatState = 'awaiting_enquiry_id';
            }
          } else if (chatState === 'awaiting_item_choice') {
            if (queryText.toLowerCase() === 'entire enquiry') {
              if (currentEnquiryId) {
                const response = await enquiryService.post('/enquiry/chatbot', { query: `recommendations for entire enquiry dbid ${currentEnquiryId}` });
                botResponseText = response.data.response;
                nextChatState = 'initial';
                setCurrentEnquiryId(null);
                setEnquiryItems([]);
              } else {
                botResponseText = "It seems I lost track of the enquiry ID. Please start over by mentioning 'certain enquiry'.";
                nextChatState = 'initial';
              }
            } else if (queryText.toLowerCase() === 'specific item') {
                if (enquiryItems.length > 0) {
                    const itemsList = enquiryItems.map(item => `- ${item.name}`).join('\n');
                    botResponseText = `Here are the items in this enquiry:\n${itemsList}\nPlease select an item below.`;
                    nextChatState = 'awaiting_item_selection';
                } else {
                    botResponseText = "This enquiry has no items, or I couldn't retrieve them. Please try 'entire enquiry' or start over.";
                    nextChatState = 'initial';
                    setCurrentEnquiryId(null);
                }
            } else {
              botResponseText = "Please choose 'entire enquiry' or 'specific item'.";
              nextChatState = 'awaiting_item_choice';
            }
          } else if (chatState === 'awaiting_item_selection') {
            const itemAndEnquiryIdMatch = queryText.match(/^item_dbid:(\d+),enquiry_dbid:(\d+)$/);

            if (itemAndEnquiryIdMatch) {
                const itemId = parseInt(itemAndEnquiryIdMatch[1], 10);
                const enquiryId = parseInt(itemAndEnquiryIdMatch[2], 10);

                const response = await enquiryService.post('/enquiry/chatbot/item-recommendation', {
                    enquiryId: enquiryId,
                    itemId: itemId
                });
                botResponseText = response.data.response;
                nextChatState = 'initial';
                setCurrentEnquiryId(null);
                setEnquiryItems([]);
            } else {
                const typedItemNameOrId = queryText.trim();
                const selectedItem = enquiryItems.find(item =>
                    item.name.toLowerCase() === typedItemNameOrId.toLowerCase() ||
                    String(item.id) === typedItemNameOrId
                );

                if (selectedItem && currentEnquiryId) {
                     const response = await enquiryService.post('/enquiry/chatbot/item-recommendation', {
                        enquiryId: currentEnquiryId,
                        itemId: selectedItem.id
                    });
                    botResponseText = response.data.response;
                    nextChatState = 'initial';
                    setCurrentEnquiryId(null);
                    setEnquiryItems([]);
                } else {
                    botResponseText = "I couldn't identify the selected item. Please try again by selecting from the options or typing the exact item name/ID.";
                    nextChatState = 'awaiting_item_selection';
                }
            }
          } else if (chatState === 'awaiting_general_query') {
            const response = await enquiryService.post('/enquiry/chatbot', { query: queryText });
            botResponseText = response.data.response;
            nextChatState = 'initial';
          }
    } catch (error) {
      console.error("Chatbot API error:", error.response?.data || error);
      botResponseText = error.response?.data?.error || 'Sorry, I encountered an error. Please try again.';
      nextChatState = 'initial';
      setCurrentEnquiryId(null);
      setEnquiryItems([]);
    } finally {
      addMessage('bot', botResponseText);
      setChatState(nextChatState);
      setIsLoading(false);
    }
  };

  const handleSendButtonClick = () => {
    if (input.trim() === '') return;
    handleUserInput(input);
  };

  const handleOptionClick = (optionText) => {
    handleUserInput(optionText);
  };

  return (
    <>
      <IconButton
        onClick={toggleChat}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          backgroundColor: 'primary.main',
          color: 'white',
          '&:hover': { backgroundColor: 'primary.dark' },
          width: 64,
          height: 64,
          zIndex: 1300,
        }}
      >
        {isOpen ? <CloseIcon /> : <ChatIcon />}
      </IconButton>

      {isOpen && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 100,
            right: 24,
            width: isMaximized ? 700 : 350,
            height: isMaximized ? '80vh' : 500,
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1299,
            transition: 'width 0.3s ease, height 0.3s ease',
          }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', backgroundColor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Chat with Jarvis!</Typography>
            <IconButton onClick={handleToggleMaximize} sx={{ color: 'white' }} size="small">
              {isMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Box>
          <Box sx={{ flexGrow: 1, p: 2, overflowY: 'auto' }}>
            {messages.map((msg, index) => (
              <Box key={index} sx={{ mb: 2, textAlign: msg.sender === 'user' ? 'right' : 'left' }}>
                <Typography
                  variant="body1"
                  component="div"
                  sx={(theme) => ({
                    display: 'inline-block',
                    p: 1.5,
                    borderRadius: 2,
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    ...(msg.sender === 'user' && {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                    }),
                    ...(msg.sender === 'bot' && {
                      backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
                      color: 'text.primary',
                    }),
                  })}
                >
                  {msg.text}
                </Typography>
              </Box>
            ))}
            {isLoading && <CircularProgress size={24} />}
            <div ref={messagesEndRef} />
          </Box>
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            {chatState === 'initial' && (
              <Stack direction="column" spacing={1} sx={{ mb: 2 }}>
                <Button variant="outlined" onClick={() => handleOptionClick('Certain Enquiry')}>
                  Certain Enquiry
                </Button>
                <Button variant="outlined" onClick={() => handleOptionClick('General Recommendation')}>
                  General Recommendation
                </Button>
              </Stack>
            )}

            {chatState === 'awaiting_enquiry_id' && (
              <Typography variant="body2" sx={{ mb: 1 }}>Please enter the Enquiry ID:</Typography>
            )}

            {chatState === 'awaiting_item_choice' && (
              <Stack direction="column" spacing={1} sx={{ mb: 2 }}>
                <Button variant="outlined" onClick={() => handleOptionClick('Entire Enquiry')}>
                  Entire Enquiry
                </Button>
                <Button variant="outlined" onClick={() => handleOptionClick('Specific Item')}>
                  Specific Item
                </Button>
              </Stack>
            )}

            {chatState === 'awaiting_item_selection' && (
                <>
                    <Typography variant="body2" sx={{ mb: 1 }}>Select an item:</Typography>
                    <Stack direction="column" spacing={1} sx={{ mb: 2 }}>
                        {enquiryItems.length > 0 ? (
                            enquiryItems.map(item => (
                                <Button
                                    key={item.id}
                                    variant="outlined"
                                    onClick={() => handleOptionClick(`item_dbid:${item.id},enquiry_dbid:${currentEnquiryId}`)}
                                >
                                    {item.name}
                                </Button>
                            ))
                        ) : (
                            <Typography variant="body2" color="error">No items found for this enquiry.</Typography>
                        )}
                    </Stack>
                </>
            )}


            {chatState === 'awaiting_general_query' && (
              <Typography variant="body2" sx={{ mb: 1 }}>Please type your general query:</Typography>
            )}

            <TextField
              fullWidth
              variant="outlined"
              placeholder="Type your message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendButtonClick()}
              disabled={isLoading}
            />
            <Button fullWidth variant="contained" onClick={handleSendButtonClick} sx={{ mt: 1 }} disabled={isLoading}>
              Send
            </Button>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default Chatbot;